import { Badge, Button, Collapse, Group, Menu, Paper, Select, Skeleton, Stack, Switch, Table, Tabs, Text, TextInput, Tooltip, UnstyledButton, VisuallyHidden } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArchive, IconArrowsExchange, IconChevronDown, IconBuildingCommunity, IconCircleOff, IconDeviceTablet, IconPlayerPause, IconPlayerPlay, IconSearch, IconShieldCheck, IconTag, IconTagStarred, IconTrash } from '@tabler/icons-react';
import { useDebouncedValue, useLocalStorage } from '@mantine/hooks';
import { Code, ConnectError } from '@connectrpc/connect';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Fragment, useId, useState, type ReactNode } from 'react';
import { DeviceAssignment, DeviceStatus } from '@motionsafe/ms-console-api/common/v1/common_pb.js';
import { IntegritySignal, type Device } from '@motionsafe/ms-console-api/admin/v1/device_pb.js';

import { api } from '../../api/client';
import { ActionsTrigger, MenuAction } from '../../components/actions';
import { deviceCountsQuery, deviceQuery, errorText, orgQuery, useInvalidate } from '../../api/hooks';
import { confirmFirst } from '../../components/confirm';
import { ApproveDialog, AssignNewDialog, MoveDialog } from '../../components/device-assign';
import { LabelDialog, type LabelKind } from '../../components/device-label';
import { StatusMark, statusColor, statusWord } from '../../components/device-status';
import { useDialog } from '../../components/dialog';
import { useGridRows } from '../../components/grid';
import { IntegrityFlag } from '../../components/integrity-flag';
import { useServerPages } from '../../components/pager';
import { devicesIn } from '../../lib/counts';
import { LockedActions, lockedItem, MenuCaption, MenuLines, type MenuLine } from '../../components/locked';
import { PANE_TABS_LIST_STYLE, PaneBody, PaneDrawer, PaneHeader } from '../../components/pane';
import { EmptyState, Facts, IdBox, PageHeader, type StatProps } from '../../components/page';
import { when } from '../../lib/format';
import { hardwareEnding } from '../../lib/hardware';
import { bootloaderWord, finding } from '../../lib/integrity';
import { isStaff } from '../../lib/roles';
import { ICON_INLINE } from '../../theme';
import { Route as AppRoute, useAccess, useOrgId } from '../_app';

export const Route = createFileRoute('/_app/devices')({ component: Devices });

const COLS = 10;
const ORG_COLS = 8;
const wrapId = { whiteSpace: 'normal', overflowWrap: 'anywhere' } as const;


const assigned = (d: Device) => d.assignment === DeviceAssignment.ASSIGNED;
// A registration waiting to take over a device staff assigned: approved with the code on its screen, never assigned.
export const waitsOnAssigned = (d: Device) => !!d.replacesDeviceId && d.replacesStatus !== DeviceStatus.REVOKED && d.replacesAssignment === DeviceAssignment.ASSIGNED;

type StatusFilter = 'all' | 'active' | 'suspended' | 'unsupported' | 'revoked';
const STATUS_OF: Record<Exclude<StatusFilter, 'all'>, DeviceStatus> = { active: DeviceStatus.ACTIVE, suspended: DeviceStatus.SUSPENDED, unsupported: DeviceStatus.UNSUPPORTED, revoked: DeviceStatus.REVOKED };


function Missing({ children }: { children: ReactNode }) {
  return <Text span inherit c="dimmed">{children}</Text>;
}

// A device as its row names it: the organization's label when it has one,
// over the end of its hardware id, which is what the device reads out while
// it waits to be assigned (the details have all of it). Staff see
// MotionSafe's own label under them, and everyone sees the serial printed on
// the tablet, so a tablet in hand can be matched to its row.
export function DeviceName({ d }: { d: Device }) {
  return (
    <>
      {d.label && <Text fw={600} style={wrapId}>{d.label}</Text>}
      {d.hardwareId
        ? <Text className="id" fw={d.label ? 400 : 600} aria-label={`Device identifier ending ${hardwareEnding(d.hardwareId)}`}>{hardwareEnding(d.hardwareId)}</Text>
        : <Text className="id" maw={420} style={wrapId}>{d.id}</Text>}
      {d.staffLabel && <Text size="xs" c="dimmed" style={wrapId}>Staff label: {d.staffLabel}</Text>}
      {d.serial && <Text size="xs" c="dimmed">Serial {d.serial}</Text>}
      {d.sotiDeviceId && <Text size="xs" c="dimmed">SOTI {d.sotiDeviceId}</Text>}
    </>
  );
}

// A time that has not happened yet: four dashes in the secondary ink, so the
// column stays quiet, and the words for a screen reader.
function Never({ what }: { what: string }) {
  return <Text span inherit c="dimmed"><span aria-hidden>----</span><VisuallyHidden>{what}</VisuallyHidden></Text>;
}

// When the device last made contact, and last synced: relative, with the
// moment on hover, in plain ink like the rest of the row.
function Moment({ at, never }: { at: { seconds: bigint } | undefined; never: string }) {
  return at
    ? <Tooltip label={when(at)}><Text span inherit className="num">{when(at, { relative: true })}</Text></Tooltip>
    : <Never what={never} />;
}
// A device with no organization: waiting to be assigned, or, after a reset, for staff to approve it with its code.
export function OrglessBadge({ d }: { d: Device }) {
  return <Badge component="span" color="warning">{waitsOnAssigned(d) ? 'needs approval' : 'unassigned'}</Badge>;
}

export const LastSeen = ({ d }: { d: Device }) => <Moment at={d.lastSeen} never="never seen" />;
export const LastSync = ({ d }: { d: Device }) => <Moment at={d.lastSyncAt} never="never synced" />;

// Placeholder widths per column, so the loading rows have the table's shape.
const SKELETON_WIDTHS = [64, 24, 32, 88, 48, 40, 72, 96, 56, 0];
// Without the Integrity and Key columns, for an organization's administrators.
const ORG_SKELETON_WIDTHS = [64, 24, 88, 48, 72, 96, 56, 0];

function Devices() {
  const orgId = useOrgId();
  const { me } = AppRoute.useRouteContext();
  // Staff see each device's key generation and integrity, and change devices;
  // an organization's administrators see its devices and neither of those.
  const staff = isStaff(me);
  const org = useQuery(orgQuery(orgId));
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [q, setQ] = useState('');
  const [debounced] = useDebouncedValue(q, 200);
  // Clearing applies at once; typing waits for a pause.
  const needle = q.trim() ? debounced.trim() : '';
  const [status, setStatus] = useState<StatusFilter>('all');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);
  const devices = useServerPages({
    key: ['devices', orgId],
    narrowed: `${orgId}|${needle}|${status}|${includeRevoked}|${flaggedOnly}`,
    read: (page) => api.device.listDevices({ orgId, includeRevoked, status: status === 'all' ? DeviceStatus.UNSPECIFIED : STATUS_OF[status], query: needle, integrityFindingsOnly: flaggedOnly, ...page }),
    noun: 'devices',
  });
  // A different organization never borrows another's rows.
  const listed = devices.query.data !== undefined && !(devices.query.isPlaceholderData && devices.query.data.devices.some((d) => d.orgId !== orgId));
  const rows = listed ? devices.query.data!.devices : [];
  const counts = useQuery(deviceCountsQuery(orgId)).data;
  const groups = counts ?? [];
  // As the page stands now, so the details follow a change made from them; read alone once it is on another.
  const onPage = selected ? rows.find((d) => d.id === selected) : undefined;
  const one = useQuery({ ...deviceQuery(selected), enabled: !!selected && !onPage });
  const row = selected ? onPage ?? one.data ?? null : null;
  const open = (id: string) => { setSelected(id); setOpened(true); };
  // A tablet that is gone (deleted, or moved to another organization) takes its pane with it, so it cannot pop open again later.
  const gone = one.error instanceof ConnectError && one.error.code === Code.NotFound;
  if (selected !== null && !onPage && (gone || (one.data && one.data.orgId !== orgId))) { setSelected(null); setOpened(false); }

  const clearFilters = () => { setQ(''); setStatus('all'); setFlaggedOnly(false); };
  const gridRow = useGridRows(rows.map((d) => d.id));

  const onRecord = org.data?.devicesOnRecord;
  const count = (s: DeviceStatus) => devicesIn(groups, (g) => g.status === s);
  const active = count(DeviceStatus.ACTIVE);
  // Of the devices the list can show, as it shows them: revoked ones only when they are.
  const flagged = staff ? devicesIn(groups, (g) => g.integrityFindings && (includeRevoked || !g.revoked)) : 0;
  const stats: StatProps[] = [
    {
      label: 'Devices on record',
      value: onRecord ?? 'unknown',
      hint: 'Suspended devices count; revoked ones leave the record',
    },
    {
      label: 'Active',
      value: counts ? active : 'unknown',
      hint: 'Operators can sign in on these',
    },
    {
      label: 'Suspended',
      value: counts ? count(DeviceStatus.SUSPENDED) : 'unknown',
      hint: 'Sign-in refused until reinstated',
    },
    {
      label: 'Unsupported',
      value: counts ? count(DeviceStatus.UNSUPPORTED) : 'unknown',
      hint: 'Marked by MotionSafe staff',
    },
  ];

  const statusOptions = [
    { value: 'all', label: 'Any status' },
    { value: 'active', label: 'active' },
    { value: 'suspended', label: 'suspended' },
    { value: 'unsupported', label: 'unsupported' },
    ...(includeRevoked ? [{ value: 'revoked', label: 'revoked' }] : []),
  ];

  return (
    <>
      <PageHeader
        title="Devices"
        description={staff
          ? 'A device joins the record when MotionSafe staff assign it to the organization, under the device identifier it reads from its own hardware.'
          : 'A device joins the record when MotionSafe staff assign it to the organization, under the device identifier it reads from its own hardware. Label your devices from their menus; MotionSafe manages the rest: to suspend, revoke or move one, contact your account manager.'}
        stats={org.isPending || !counts ? 'loading' : stats}
        statsCount={stats.length}
      />

      <Group justify="space-between" align="center" gap="sm" mb="md" wrap="wrap">
        <Group gap="lg" wrap="wrap" flex="1 1 280px">
          <TextInput
            aria-label="Search devices"
            placeholder="Search devices"
            leftSection={<IconSearch {...ICON_INLINE} />}
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            w={{ base: '100%', xs: 280 }}
          />
          <Switch
            label="Show revoked"
            checked={includeRevoked}
            onChange={(e) => {
              const on = e.currentTarget.checked;
              setIncludeRevoked(on);
              if (!on && status === 'revoked') setStatus('all');
            }}
          />
          {staff && (
            <Switch
              label={`Integrity findings only${flagged ? ` (${flagged})` : ''}`}
              checked={flaggedOnly}
              onChange={(e) => setFlaggedOnly(e.currentTarget.checked)}
            />
          )}
        </Group>
        <Group gap="sm" wrap="wrap">
          <Select aria-label="Filter by status" data={statusOptions} value={status} allowDeselect={false} w={168}
            onChange={(v) => setStatus((v ?? 'all') as StatusFilter)} />
        </Group>
      </Group>

      <Paper p={0} style={{ overflow: 'hidden' }}>
        {listed && devices.total === 0 && !needle && status === 'all' && !flaggedOnly ? (
          <EmptyState
            icon={IconDeviceTablet}
            title="No devices on record"
            body="Nothing to add from here: a tablet joins the record the first time one of your operators signs in on it, and appears here from then on."
          />
        ) : listed && rows.length === 0 ? (
          <EmptyState
            icon={IconSearch}
            title="No device matches"
            body={needle ? `Nothing on record matches “${q.trim()}” with these filters.` : 'Nothing on record matches these filters.'}
            action={<Button variant="default" onClick={clearFilters}>Clear filters</Button>}
          />
        ) : (
          <Table.ScrollContainer minWidth={staff ? 1168 : 984}>
            <Table role="grid" layout="fixed" aria-label="Devices on this organization's record" aria-busy={!listed || devices.query.isPlaceholderData} highlightOnHover={rows.length > 0} style={{ opacity: devices.query.isPlaceholderData ? 0.6 : 1, transition: 'opacity var(--motion-micro, 120ms) ease-out' }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Device</Table.Th><Table.Th w={72}>Status</Table.Th>{staff && <Table.Th w={96}>Integrity</Table.Th>}<Table.Th w={110}>Model</Table.Th><Table.Th w={90}>App</Table.Th>{staff && <Table.Th w={90}>Key</Table.Th>}<Table.Th w={130}>Last seen</Table.Th><Table.Th w={130}>Last sync</Table.Th><Table.Th w={130}>Outbox</Table.Th><Table.Th w={52}><VisuallyHidden>Actions</VisuallyHidden></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((d) => {
                  const isSelected = opened && d.id === selected;
                  return (
                    <Table.Tr key={d.id} {...gridRow(d.id, () => open(d.id))} aria-selected={isSelected} onClick={() => open(d.id)} style={{ cursor: 'pointer' }} {...(isSelected ? { bg: 'var(--mantine-primary-color-light)' } : {})}>
                      <Table.Td><DeviceName d={d} /></Table.Td>
                      <Table.Td><StatusMark d={d} /></Table.Td>
                      {staff && <Table.Td><IntegrityFlag d={d} /></Table.Td>}
                      <Table.Td>{d.model || <Missing>unknown</Missing>}</Table.Td>
                      <Table.Td className="id">{d.appVersion || <Missing>unknown</Missing>}</Table.Td>
                      {staff && <Table.Td className="id">{d.keyringGeneration || <Missing>none</Missing>}</Table.Td>}
                      <Table.Td className="num" style={{ whiteSpace: 'nowrap' }}><LastSeen d={d} /></Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}><LastSync d={d} /></Table.Td>
                      <Table.Td className="num">{d.outboxDepth > 0 ? `${d.outboxDepth} waiting` : <Missing>empty</Missing>}</Table.Td>
                      {/* The menu is the row's own: a click or a key in it does not open the details. */}
                      <Table.Td ta="right" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                        <DeviceActions device={d} compact />
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
                {!listed && !devices.query.isError && [0, 1, 2, 3, 4].map((i) => (
                  <Table.Tr key={`loading-${i}`} bg="transparent">
                    {(staff ? SKELETON_WIDTHS : ORG_SKELETON_WIDTHS).map((w, j) => (
                      <Table.Td key={j}>
                        {i === 0 && j === 0 && <VisuallyHidden>Loading devices</VisuallyHidden>}
                        <Skeleton height={14} width={w} radius="sm" my={5} />
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))}
                {devices.query.isError && (
                  <Table.Tr>
                    <Table.Td colSpan={staff ? COLS : ORG_COLS}>
                      <Group gap="sm" justify="center" py="lg">
                        <Text size="sm" c="danger">{errorText(devices.query.error)}</Text>
                        <Button variant="subtle" size="compact-sm" onClick={() => void devices.query.refetch()} disabled={devices.query.isFetching}>Try again</Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        {devices.footer}
      </Paper>

      <PaneDrawer opened={opened && row !== null} onClose={() => setOpened(false)}>
        {row && <DevicePane key={row.id} device={row} />}
      </PaneDrawer>
    </>
  );
}

// What can be done to a device, from one kebab: on its row and at the top
// of its details.
export function DeviceActions({ device, compact = false }: { device: Device; /** The row's smaller mark. */ compact?: boolean }) {
  const inv = useInvalidate();
  const { me } = AppRoute.useRouteContext();
  const staff = isStaff(me);
  // Changing a device is Fulfillment's; revoking one, which is permanent, an admin's.
  const access = useAccess();
  const write = access.may('SetDeviceStatus');
  const mayRevoke = access.may('RevokeDevice');
  const lockedCaption = useId();
  const set = useMutation({
    mutationFn: (status: DeviceStatus) => api.device.setDeviceStatus({ deviceId: device.id, status, note: 'from console' }),
    // The count on record and the keys' device counts move with a status, so
    // the organization and the keys are refetched with the list.
    onSuccess: (_r, status) => {
      notifications.show({ message: status === DeviceStatus.ACTIVE ? 'Device reinstated. Operators can sign in on it again.' : status === DeviceStatus.OUT_OF_CIRCULATION ? 'Device out of circulation. It belongs to no organization, and clears what it holds at its next contact.' : `Device ${statusWord(status)}. Its sessions end at the next contact and sign-in is refused.` });
      if (status === DeviceStatus.OUT_OF_CIRCULATION) return inv('devices', 'org', 'orgs', 'keys');
      return inv('devices', 'org', 'keys');
    },
    onError: (e) => notifications.show({ message: errorText(e), color: 'danger' }),
  });
  const st = device.status;
  // Move, and Assign for a device out of circulation, which goes to its next organization the same way.
  const moveDialog = useDialog();
  // Assign for a device that has just registered.
  const assignDialog = useDialog();
  // Approve for a reset tablet waiting to take its record back.
  const approveDialog = useDialog();
  // Either label, whichever was chosen.
  const labelDialog = useDialog();
  const [labelKind, setLabelKind] = useState<LabelKind>('label');
  const openLabel = (kind: LabelKind) => { setLabelKind(kind); labelDialog.open(); };
  const remove = useMutation({
    mutationFn: () => api.device.deleteDevice({ deviceId: device.id }),
    onSuccess: () => { notifications.show({ message: 'Device deleted.' }); return inv('devices'); },
    onError: (e) => notifications.show({ message: errorText(e), color: 'danger' }),
  });
  const askDelete = () => confirmFirst({
    title: 'Delete this device?',
    body: device.movePending
      ? `${device.model || 'Device'} ${device.hardwareId} has not been in contact since it left its organization, so it has not cleared what that organization left on it. Delete it only if it is not coming back: once its record is gone it is never told to clear.`
      : `${device.model || 'Device'} ${device.hardwareId} is removed from the record. If it is still in use, it registers again the next time its app opens and comes back unassigned.`,
    label: 'Delete',
    danger: true,
    onConfirm: () => remove.mutateAsync(),
  });
  // An org admin reinstates a tablet they suspended; one MotionSafe staff
  // marked unsupported is reinstated by staff, and shows locked otherwise.
  const canReinstate = st === DeviceStatus.SUSPENDED || (staff && st === DeviceStatus.UNSUPPORTED);
  const suspend = () => confirmFirst({
    title: 'Suspend this tablet?',
    body: 'Every session on it ends at its next contact and the app refuses sign-in before anyone types a password, naming you as the contact. Collections on its card and the work in its storage are untouched. Reinstate it here when the issue is resolved. Recorded in the audit trail under your name.',
    label: 'Suspend',
    danger: true,
    onConfirm: () => set.mutateAsync(DeviceStatus.SUSPENDED),
  });
  const reinstate = () => confirmFirst({
    title: 'Reinstate this tablet?',
    body: 'Operators can sign in on it again from its next check. Nothing else changes.',
    label: 'Reinstate',
    onConfirm: () => set.mutateAsync(DeviceStatus.ACTIVE),
  });
  const unsupported = () => confirmFirst({
    title: 'Mark this tablet unsupported?',
    body: 'The app tells the operator the tablet is no longer supported and refuses sign-in. Use this for hardware the fleet is retiring; a version too old to sign in is refused by the support policy without marking it. Reversible.',
    label: 'Mark unsupported',
    onConfirm: () => set.mutateAsync(DeviceStatus.UNSUPPORTED),
  });
  const retire = () => confirmFirst({
    title: 'Take this device out of circulation?',
    body: 'The device leaves its organization and belongs to none. Everyone is signed out of it, nobody can sign in, and at its next contact it clears everything it holds: locked sessions, PINs, the addresses it remembers, its key, and every operator\'s parsed data. What was collected on its card stays on the card. Put it back into use by assigning it to an organization. Recorded in the audit trail under your name.',
    label: 'Take out of circulation',
    danger: true,
    onConfirm: () => set.mutateAsync(DeviceStatus.OUT_OF_CIRCULATION),
  });
  const revoke = () => confirmFirst({
    title: 'Revoke this tablet permanently?',
    body: 'This cannot be undone. Every session ends at its next contact and this tablet cannot register again with the same key. Use it for a lost, stolen, or destroyed tablet; suspend instead if it may come back. Collections on its card and the work in its storage are untouched. Recorded in the audit trail under your name.',
    label: 'Revoke',
    danger: true,
    onConfirm: () => set.mutateAsync(DeviceStatus.REVOKED),
  });

  const label = `Actions for device ${device.hardwareId || device.id}`;
  // The organization's label, for a device that has one; MotionSafe's own, for staff.
  const labelLines: MenuLine[] = [
    ...(device.orgId ? [{ label: 'Label', icon: IconTag, onClick: access.may('SetDeviceLabel') ? () => openLabel('label') : undefined, caption: access.caption('SetDeviceLabel') }] : []),
    ...(staff ? [{ label: 'Staff label', icon: IconTagStarred, onClick: access.may('SetDeviceStaffLabel') ? () => openLabel('staff') : undefined, caption: access.caption('SetDeviceStaffLabel') }] : []),
  ];
  // First in every branch, so an assignment that gives the device an
  // organization does not take its dialog away mid-fade.
  const dialogs = (
    <>
      {moveDialog.mounted && <MoveDialog opened={moveDialog.opened} round={moveDialog.round} device={device} onClose={moveDialog.close} />}
      {assignDialog.mounted && <AssignNewDialog opened={assignDialog.opened} round={assignDialog.round} device={device} orgId="" onClose={assignDialog.close} />}
      {approveDialog.mounted && <ApproveDialog opened={approveDialog.opened} round={approveDialog.round} device={device} onClose={approveDialog.close} />}
      {labelDialog.mounted && <LabelDialog opened={labelDialog.opened} round={labelDialog.round} device={device} kind={labelKind} staff={staff} onClose={labelDialog.close} />}
    </>
  );
  // Labelling is the one change to a device that is not MotionSafe's alone,
  // and a revoked device is still named in its organization's trail.
  const labelMenu = (
    <>
      {dialogs}
      <Menu position="bottom-end">
        <Menu.Target>
          <ActionsTrigger label={label} {...(compact ? { size: 28 } : {})} />
        </Menu.Target>
        <Menu.Dropdown>
          <MenuLines lines={labelLines} />
        </Menu.Dropdown>
      </Menu>
    </>
  );
  if (!staff || st === DeviceStatus.REVOKED) return labelMenu;
  if (!device.orgId) {
    if (!access.may('AssignDevice')) return <LockedActions compact={compact} label={label} caption={access.caption('AssignDevice')} groups={[['Assign', 'Staff label'], ['Delete']]} />;
    // The wipe a device owes is kept on its record, so the record stays until the device has been told.
    const deletable = access.may('DeleteDevice') && (!device.movePending || access.unrestricted);
    return (
      <>
        {dialogs}
        <Menu position="bottom-end">
          <Menu.Target>
            <ActionsTrigger label={label} loading={remove.isPending} {...(compact ? { size: 28 } : {})} />
          </Menu.Target>
          <Menu.Dropdown>
            {/* A reset tablet waiting on an assigned device is approved with its code; any other is assigned. */}
            {waitsOnAssigned(device)
              ? <MenuLines lines={[{ label: 'Approve device', icon: IconShieldCheck, onClick: access.may('ApproveDeviceTakeover') ? approveDialog.open : undefined, caption: access.caption('ApproveDeviceTakeover') }]} />
              : <MenuAction icon={IconBuildingCommunity} onClick={st === DeviceStatus.OUT_OF_CIRCULATION ? moveDialog.open : assignDialog.open}>Assign</MenuAction>}
            <MenuLines lines={labelLines} />
            <Menu.Divider />
            <MenuLines lines={[{ label: 'Delete', icon: IconTrash, danger: true, onClick: deletable ? askDelete : undefined, caption: device.movePending ? 'It can be deleted once it has cleared what its last organization left on it, at its next contact.' : access.caption('DeleteDevice') }]} />
          </Menu.Dropdown>
        </Menu>
      </>
    );
  }
  return (
    <>
        {dialogs}
        {!write && <LockedActions compact={compact} label={label} caption={access.caption('SetDeviceStatus')} groups={[['Label', 'Staff label'], ...lockedDeviceActions(st)]} />}
        {write && (
          <Menu position="bottom-end">
            <Menu.Target>
              <ActionsTrigger label={label} loading={set.isPending} {...(compact ? { size: 28 } : {})} />
            </Menu.Target>
            <Menu.Dropdown>
              <MenuLines lines={labelLines} />
              <Menu.Divider />
              {st === DeviceStatus.ACTIVE && <MenuAction icon={IconPlayerPause} onClick={suspend}>Suspend</MenuAction>}
              {canReinstate && <MenuAction icon={IconPlayerPlay} onClick={reinstate}>Reinstate</MenuAction>}
              {staff && st !== DeviceStatus.UNSUPPORTED && <MenuAction icon={IconCircleOff} onClick={unsupported}>Mark unsupported</MenuAction>}
              {!staff && st === DeviceStatus.UNSUPPORTED && (
                <>
                  <Menu.Item {...lockedItem(lockedCaption)}>Reinstate</Menu.Item>
                  <MenuCaption id={lockedCaption}>Only MotionSafe staff reinstate a device they marked unsupported. Contact your account manager.</MenuCaption>
                </>
              )}
              {staff && assigned(device) && <MenuAction icon={IconArrowsExchange} onClick={moveDialog.open}>Move</MenuAction>}
              {staff && st !== DeviceStatus.OUT_OF_CIRCULATION && <MenuAction icon={IconArchive} onClick={retire}>Take out of circulation</MenuAction>}
              <Menu.Divider />
              <MenuLines lines={[{ label: 'Revoke permanently', icon: IconTrash, danger: true, onClick: mayRevoke ? revoke : undefined, caption: 'Revoking is permanent, so only a MotionSafe admin can do it.' }]} />
            </Menu.Dropdown>
          </Menu>
        )}
    </>
  );
}

// A device's details. Each part is a tab of its own, so a tab is the only
// heading: Device (which tablet it is, and why it is not in use when it is
// not) and Activity for everyone; Record, Connection and Integrity besides
// for MotionSafe staff. Integrity findings show as a red or amber dot on
// their tab. The tab last chosen stays chosen from one device to
// the next, so staff checking one thing across devices click once.
export function DevicePane({ device }: { device: Device }) {
  const { me } = AppRoute.useRouteContext();
  const staff = isStaff(me);
  const model = device.model ? `${device.model} tablet` : 'Tablet';
  // One line under the name: the model when a label names it, the app, and
  // where it stands (the identifier is on the Device tab):
  // its status, or for a device with no organization, whether it waits to be
  // assigned or approved. The sync time is on the Activity tab.
  const orgless = !device.orgId && device.status === DeviceStatus.ACTIVE;
  const stateWord = orgless ? (waitsOnAssigned(device) ? 'needs approval' : 'unassigned') : statusWord(device.status);
  const stateColor = orgless ? 'warning' : statusColor(device.status);
  const parts: ReactNode[] = [
    ...(device.label ? [model] : []),
    ...(device.appVersion ? [`App ${device.appVersion}`] : []),
    (
      <Text key="state" span inherit c={`var(--mantine-color-${stateColor}-text)`} fw={600} style={{ whiteSpace: 'nowrap' }}>
        {stateWord.charAt(0).toUpperCase() + stateWord.slice(1)}
      </Text>
    ),
  ];
  const subtitle = parts.map((p, i) => <Fragment key={i}>{i > 0 && ' · '}{p}</Fragment>);
  const findings = staff && device.integrity ? device.integrityFindings : [];
  const critical = findings.some((f) => f.critical);
  const tabs: { value: string; label: string; badge?: ReactNode; panel: ReactNode }[] = [
    { value: 'device', label: 'Device', panel: <DeviceFacts device={device} staff={staff} /> },
    { value: 'activity', label: 'Activity', panel: <ActivityFacts device={device} /> },
    ...(staff ? [
      { value: 'record', label: 'Record', panel: <RecordFacts device={device} /> },
      { value: 'connection', label: 'Connection', panel: <ConnectionFacts device={device} /> },
      {
        value: 'integrity',
        label: 'Integrity',
        badge: findings.length > 0 ? (
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: `var(--mantine-color-${critical ? 'danger' : 'warning'}-filled)` }}>
            <VisuallyHidden>{`${findings.length} ${findings.length === 1 ? 'finding' : 'findings'}`}</VisuallyHidden>
          </span>
        ) : undefined,
        panel: <IntegrityFacts device={device} />,
      },
    ] : []),
  ];
  const [chosen, setChosen] = useLocalStorage<string>({ key: 'device-pane-tab', defaultValue: 'device' });
  const tab = tabs.some((t) => t.value === chosen) ? chosen : 'device';

  return (
    <>
      <PaneHeader
        title={device.label || model}
        subtitle={subtitle}
        actions={<DeviceActions device={device} />}
        divider={false}
      />
      <PaneBody>
        <Tabs value={tab} onChange={(v) => setChosen(v ?? 'device')} keepMounted={false}>
          <Tabs.List aria-label="Device details" grow style={PANE_TABS_LIST_STYLE}>
            {tabs.map((t) => <Tabs.Tab key={t.value} value={t.value} rightSection={t.badge}>{t.label}</Tabs.Tab>)}
          </Tabs.List>
          {tabs.map((t) => (
            <Tabs.Panel key={t.value} value={t.value} pt="lg">
              <Stack gap="md">{t.panel}</Stack>
            </Tabs.Panel>
          ))}
        </Tabs>
      </PaneBody>
    </>
  );
}

// Which tablet it is and how to find it on the bench; first, when it is not
// in use, why and since when. The device identifier shows as its last six
// characters, as the tablet's own screen shows them, so what is read out
// over the phone matches at a glance. MotionSafe staff can open the whole of
// it beneath, to copy or compare; nobody else is sent more than the six.
// Staff also read the capabilities it last reported, once it has.
function DeviceFacts({ device, staff }: { device: Device; staff: boolean }) {
  const notInUse = device.status !== DeviceStatus.ACTIVE;
  const [whole, setWhole] = useState(false);
  const wholeId = useId();
  const identifier = device.hardwareId && !staff
    ? <Text span className="id" size="sm" fw={600}>{hardwareEnding(device.hardwareId)}</Text>
    : device.hardwareId
    ? (
      <UnstyledButton onClick={() => setWhole((w) => !w)} aria-expanded={whole} aria-controls={wholeId} className="id-ending">
        <Text span className="id" size="sm" fw={600}>{hardwareEnding(device.hardwareId)}</Text>
        <IconChevronDown size={14} stroke={2} aria-hidden className="id-ending-chevron" data-open={whole || undefined} />
        <VisuallyHidden>{whole ? 'Hide the whole identifier' : 'Show the whole identifier'}</VisuallyHidden>
      </UnstyledButton>
    )
    : <Missing>not reported</Missing>;
  return (
    <>
      <Stack gap={0}>
        <Facts rows={[
          ...(notInUse && device.statusNote ? [{ label: 'Status note', value: device.statusNote }] : []),
          ...(notInUse && device.revokedAt ? [{ label: 'Revoked', value: when(device.revokedAt) }] : notInUse && device.statusChangedAt ? [{ label: 'Status since', value: when(device.statusChangedAt) }] : []),
          ...(device.orgId ? [{ label: 'Label', value: device.label || <Missing>none</Missing> }] : []),
          ...(staff ? [{ label: 'Staff label', value: device.staffLabel || <Missing>none</Missing> }] : []),
          { label: 'Serial number', value: device.serial || <Missing>not reported</Missing>, mono: !!device.serial },
          { label: 'Device identifier', value: identifier },
          ...(staff ? capabilitiesFacts(device) : []),
        ]} />
        {staff && device.hardwareId && (
          <Collapse expanded={whole} transitionDuration={200} transitionTimingFunction="var(--ease-out)" id={wholeId}>
            <IdBox value={device.hardwareId} label="Device identifier" mt={8} />
          </Collapse>
        )}
      </Stack>
      {/* Out of circulation also waits to clear the tablet, but belongs nowhere; its status note says so. */}
      {device.movePending && !!device.orgId && <Text size="xs" c="dimmed">Assigned here{device.movedAt ? ` ${when(device.movedAt)}` : ''} and not in contact since. At its next contact it clears what its last organization left on it; nobody can sign in on it until then.</Text>}
    </>
  );
}

// What the device last said of its capabilities (ARCHITECTURE.md §15.6): the
// container version its platform runs, the release from this console it last
// installed, and whether that install waits for a restart. Nothing for a
// device that has never said; the service sends these to staff alone.
function capabilitiesFacts(device: Device): { label: string; value: ReactNode; mono?: boolean }[] {
  if (!device.capabilitiesReportedAt) return [];
  return [
    { label: 'Capabilities version', value: device.capabilitiesContainerVersion > 0n ? device.capabilitiesContainerVersion.toString() : <Missing>the system's own, or not known</Missing> },
    { label: 'Capabilities release', value: device.capabilitiesReleaseId || <Missing>none from this console</Missing>, mono: !!device.capabilitiesReleaseId },
    ...(device.capabilitiesRestartPending ? [{ label: 'Capabilities install', value: 'Installed, waits for a restart' }] : []),
    { label: 'Capabilities reported', value: when(device.capabilitiesReportedAt, { relative: true }) },
  ];
}

// Whether it is in touch.
function ActivityFacts({ device }: { device: Device }) {
  return (
    <Facts rows={[
      { label: 'Last sync', value: device.lastSyncAt ? when(device.lastSyncAt) : <Missing>never</Missing> },
      { label: 'Last seen', value: when(device.lastSeen) || <Missing>not recorded</Missing> },
      { label: 'Outbox', value: device.outboxDepth > 0 ? `${device.outboxDepth} ${device.outboxDepth === 1 ? 'event' : 'events'} waiting` : 'nothing waiting' },
      { label: 'First seen', value: when(device.firstSeen) || <Missing>not recorded</Missing> },
    ]} />
  );
}

// The record behind a device, for MotionSafe staff alone: the ids support
// looks it up by, and how it came to its organization.
function RecordFacts({ device }: { device: Device }) {
  return (
    <Facts rows={[
      { label: 'Record ID', value: device.id, mono: true },
      ...(device.sotiDeviceId ? [{ label: 'SOTI ID', value: device.sotiDeviceId, mono: true }] : []),
      { label: 'Model', value: device.model || <Missing>not reported</Missing> },
      ...(assigned(device) && device.assignedBy ? [{ label: 'Assigned by', value: device.assignedAt ? `${device.assignedBy}, ${when(device.assignedAt)}` : device.assignedBy }] : []),
      ...(device.assignmentNote ? [{ label: 'Assignment note', value: device.assignmentNote }] : []),
      ...(device.movedAt && !device.movePending ? [{ label: 'Moved here', value: when(device.movedAt) }] : []),
    ]} />
  );
}

// How it talks to the console, for MotionSafe staff alone: the organization
// key it last fetched, and mutual TLS.
function ConnectionFacts({ device }: { device: Device }) {
  return (
    <Facts rows={[
      { label: 'Organization key', value: device.keyringGeneration || <Missing>none fetched</Missing>, mono: !!device.keyringGeneration },
      ...(assigned(device) ? [
        { label: 'Certificate', value: device.mtlsCertificateNotAfter ? `Ends ${when(device.mtlsCertificateNotAfter)}` : <Missing>none issued</Missing> },
        { label: 'Device key', value: device.mtlsCapable ? 'Can sign a TLS handshake' : 'Cannot sign a TLS handshake yet' },
      ] : []),
    ]} />
  );
}

// What the device says about its own boot and build, for MotionSafe staff
// alone. A reading the service finds is its state word on a badge, red for
// an unlocked or rooted tablet and amber for a way in that is turned on; the
// rest reads plainly, so the one that matters is the one that stands out.
function IntegrityFacts({ device }: { device: Device }) {
  const r = device.integrity;
  if (!r) return <Text size="sm" c="dimmed">Not reported yet. A device reports it when it registers and at every sync, once its app is up to date.</Text>;
  const shown = (level: 'critical' | 'warning' | null, word: string): ReactNode => (level
    ? <Badge component="span" color={level === 'critical' ? 'danger' : 'warning'}>{word}</Badge>
    : word);
  const onOff = (signal: IntegritySignal, on: boolean) => shown(finding(device, signal), on ? 'on' : 'off');
  const bootloader = bootloaderWord(r.vbmetaDeviceState, r.flashLocked);
  const build = [r.buildType, r.buildTags, r.debuggable === '1' ? 'debuggable' : ''].filter(Boolean).join(', ');
  const unread = <Missing>not readable</Missing>;
  return (
    <>
      <Facts rows={[
        { label: 'Bootloader', value: bootloader ? shown(finding(device, IntegritySignal.BOOTLOADER_UNLOCKED), bootloader) : unread },
        { label: 'Verified boot', value: r.verifiedBootState ? shown(finding(device, IntegritySignal.VERIFIED_BOOT), r.verifiedBootState) : unread },
        { label: 'Build', value: build ? shown(finding(device, IntegritySignal.DEBUGGABLE_BUILD, IntegritySignal.TEST_KEYS), build) : unread },
        { label: 'Signs of root', value: r.rootTraces.length
          ? (
            <Stack gap={4} align="flex-end">
              {shown('critical', 'found')}
              {r.rootTraces.map((t) => <Text key={t} span size="sm" className="id">{t}</Text>)}
            </Stack>
          )
          : 'none found' },
        { label: 'Developer options', value: onOff(IntegritySignal.DEVELOPER_OPTIONS, r.developerOptions) },
        { label: 'USB debugging', value: onOff(IntegritySignal.USB_DEBUGGING, r.usbDebugging) },
        { label: 'Wireless debugging', value: onOff(IntegritySignal.WIRELESS_DEBUGGING, r.wirelessDebugging) },
        { label: 'Reported', value: when(device.integrityReportedAt) || <Missing>not recorded</Missing> },
      ]} />
      <Text size="xs" c="dimmed">The device reports this about itself, and root can hide it, so it is a sign, not proof. Nothing is refused on it, and its organization does not see it.</Text>
    </>
  );
}

// Read-only staff: every status change a MotionSafe admin has here, locked,
// under one caption at the top of the menu.
function lockedDeviceActions(status: DeviceStatus): string[][] {
  const change = [
    ...(status === DeviceStatus.ACTIVE ? ['Suspend'] : []),
    ...(status === DeviceStatus.SUSPENDED || status === DeviceStatus.UNSUPPORTED ? ['Reinstate'] : []),
    ...(status !== DeviceStatus.UNSUPPORTED ? ['Mark unsupported'] : []),
  ];
  return [change, ['Revoke permanently']];
}
